import { useEffect, useRef, useState, useCallback } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";

const useAgora = ({ appId, channel, token = null, uid = null, role = "host" }) => {
  const clientRef = useRef(null);
  const [localTracks, setLocalTracks] = useState({ audioTrack: null, videoTrack: null });
  const [screenTrack, setScreenTrack] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [joined, setJoined] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [error, setError] = useState(null);
  const [connectionState, setConnectionState] = useState("DISCONNECTED");

  // Initialize Agora client
  useEffect(() => {
    const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
    clientRef.current = client;

    client.on("connection-state-change", (state) => {
      setConnectionState(state);
    });

    client.on("user-published", async (user, mediaType) => {
      await client.subscribe(user, mediaType);
      setRemoteUsers((prev) => {
        const exists = prev.find((u) => u.uid === user.uid);
        if (exists) {
          return prev.map((u) => (u.uid === user.uid ? user : u));
        }
        return [...prev, user];
      });
    });

    client.on("user-unpublished", (user, mediaType) => {
      setRemoteUsers((prev) =>
        prev.map((u) => (u.uid === user.uid ? user : u))
      );
    });

    client.on("user-left", (user) => {
      setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
    });

    client.on("user-joined", (user) => {
      setRemoteUsers((prev) => {
        if (!prev.find((u) => u.uid === user.uid)) {
          return [...prev, user];
        }
        return prev;
      });
    });

    return () => {
      client.removeAllListeners();
    };
  }, []);

  const join = useCallback(async () => {
    if (!clientRef.current || joined) return;
    try {
      setError(null);

      // For Agora projects set to "Testing mode" (no auth / static key),
      // the token MUST be passed as null — not an empty string.
      // For projects set to "Secure mode" (dynamic key), pass the real token string.
      const resolvedToken = token && token.trim() !== "" ? token.trim() : null;

      const generatedUid = await clientRef.current.join(appId, channel, resolvedToken, uid);

      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
        { encoderConfig: "high_quality" },
        { encoderConfig: "720p_2" }
      );

      await clientRef.current.publish([audioTrack, videoTrack]);

      setLocalTracks({ audioTrack, videoTrack });
      setJoined(true);
      return generatedUid;
    } catch (err) {
      setError(err.message || "Failed to join channel");
      throw err;
    }
  }, [appId, channel, token, uid, joined]);

  const leave = useCallback(async () => {
    if (!clientRef.current || !joined) return;
    try {
      if (localTracks.audioTrack) {
        localTracks.audioTrack.close();
      }
      if (localTracks.videoTrack) {
        localTracks.videoTrack.close();
      }
      if (screenTrack) {
        screenTrack.close();
      }
      await clientRef.current.leave();
      setLocalTracks({ audioTrack: null, videoTrack: null });
      setScreenTrack(null);
      setRemoteUsers([]);
      setJoined(false);
      setIsSharingScreen(false);
      setIsAudioMuted(false);
      setIsVideoMuted(false);
    } catch (err) {
      setError(err.message || "Failed to leave channel");
    }
  }, [joined, localTracks, screenTrack]);

  const toggleAudio = useCallback(async () => {
    if (!localTracks.audioTrack) return;
    if (isAudioMuted) {
      await localTracks.audioTrack.setEnabled(true);
    } else {
      await localTracks.audioTrack.setEnabled(false);
    }
    setIsAudioMuted((prev) => !prev);
  }, [localTracks.audioTrack, isAudioMuted]);

  const toggleVideo = useCallback(async () => {
    if (!localTracks.videoTrack) return;
    if (isVideoMuted) {
      await localTracks.videoTrack.setEnabled(true);
    } else {
      await localTracks.videoTrack.setEnabled(false);
    }
    setIsVideoMuted((prev) => !prev);
  }, [localTracks.videoTrack, isVideoMuted]);

  const startScreenShare = useCallback(async () => {
    if (!clientRef.current || !joined || isSharingScreen) return;
    try {
      const screenVideoTrack = await AgoraRTC.createScreenVideoTrack(
        { encoderConfig: "1080p_1" },
        "auto"
      );

      // Unpublish camera, publish screen
      if (localTracks.videoTrack) {
        await clientRef.current.unpublish(localTracks.videoTrack);
      }

      // Handle if screenVideoTrack is an array (with audio) or single track
      const trackToPublish = Array.isArray(screenVideoTrack)
        ? screenVideoTrack[0]
        : screenVideoTrack;

      await clientRef.current.publish(trackToPublish);
      setScreenTrack(trackToPublish);
      setIsSharingScreen(true);

      // Handle user stopping screen share via browser UI
      trackToPublish.on("track-ended", async () => {
        await stopScreenShare();
      });
    } catch (err) {
      if (err.name !== "NotAllowedError") {
        setError(err.message || "Failed to start screen sharing");
      }
    }
  }, [clientRef, joined, isSharingScreen, localTracks.videoTrack]);

  const stopScreenShare = useCallback(async () => {
    if (!clientRef.current || !screenTrack) return;
    try {
      await clientRef.current.unpublish(screenTrack);
      screenTrack.close();

      // Re-publish camera video
      if (localTracks.videoTrack) {
        await clientRef.current.publish(localTracks.videoTrack);
      }

      setScreenTrack(null);
      setIsSharingScreen(false);
    } catch (err) {
      setError(err.message || "Failed to stop screen sharing");
    }
  }, [clientRef, screenTrack, localTracks.videoTrack]);

  const toggleScreenShare = useCallback(async () => {
    if (isSharingScreen) {
      await stopScreenShare();
    } else {
      await startScreenShare();
    }
  }, [isSharingScreen, startScreenShare, stopScreenShare]);

  return {
    client: clientRef.current,
    localTracks,
    screenTrack,
    remoteUsers,
    joined,
    isAudioMuted,
    isVideoMuted,
    isSharingScreen,
    error,
    connectionState,
    join,
    leave,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
  };
};

export default useAgora;